import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FieldLabel } from '@puckeditor/core'
import { Image, Loader2, Trash2, Upload, X } from 'lucide-react'
import { doPost2WithFormData, doDelete } from 'services/common'
import { adminRequest } from 'utils/adminRequest'
import * as styles from './FileUploadField.module.scss'

export type UploadedFile =
  | {
      filename: string
      id: string
      link: string
      path: string
    }
  | string

export type FileUploadFieldType = {
  type: 'fileUpload'
  label: string
  /** MIME types or extensions, e.g. { 'image/*': ['.png', '.jpg'] } */
  accept?: Record<string, string[] | readonly string[]>
  /** Max file size in bytes */
  maxSize?: number
  /** Path to the file in the bucket */
  path?: string
}

const UPLOAD_PATH = '/admin/image'

function getFileName(file: UploadedFile): string {
  if (typeof file === 'string') return file
  return file.filename
}

export function getPuckFileUrl(file: UploadedFile): string {
  if (typeof file === 'string') return file
  return file?.link ?? ''
}

const DELETE_CONFIRM_MESSAGE =
  'Are you sure you want to delete this file? This action cannot be undone.'

export async function deleteUploadedFile(
  file: UploadedFile,
  onDeleted: () => void,
): Promise<void> {
  if (typeof file === 'string') {
    onDeleted()
    return
  }
  const confirmed = window.confirm(DELETE_CONFIRM_MESSAGE)
  if (!confirmed) return
  try {
    await adminRequest(async () => {
      const response = await doDelete(`${UPLOAD_PATH}/${file.id}`)
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `Delete failed (${response.status})`)
      }
      onDeleted()
    })
  } catch {
    onDeleted()
  }
}

const DEFAULT_ACCEPT = {
  'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
}
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024 // 5MB
const DEFAULT_PATH = '/puck/'

export type FileUploadInputProps = {
  value: UploadedFile | undefined
  onChange: (value: UploadedFile | undefined) => void
  accept?: Record<string, string[] | readonly string[]>
  maxSize?: number
  path?: string
  ariaLabel?: string
}

export const FileUploadInput: React.FC<FileUploadInputProps> = ({
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  maxSize = DEFAULT_MAX_SIZE,
  path = DEFAULT_PATH,
  ariaLabel = 'Upload file',
}) => {
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleUpload = useCallback(
    async (file: File) => {
      setUploadError(null)
      setIsUploading(true)
      try {
        await adminRequest(async () => {
          const uniqueFileName = `${Date.now()}-${file.name}`
          const response = await doPost2WithFormData(UPLOAD_PATH, {
            file,
            path,
            renameFile: true,
            fileName: uniqueFileName,
          })

          if (!response.ok) {
            const text = await response.text()
            throw new Error(text || `Upload failed (${response.status})`)
          }

          const data = await response.json()
          onChange(data)
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed'
        setUploadError(message)
      } finally {
        setIsUploading(false)
      }
    },
    [onChange, path],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (file) handleUpload(file)
    },
    [handleUpload],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    maxSize,
    maxFiles: 1,
    noClick: true,
    disabled: !!value || isUploading,
    onDropRejected: fileRejections => {
      const msg = fileRejections[0]?.errors[0]?.message ?? 'File rejected'
      setUploadError(msg)
    },
  })

  const handleClear = useCallback(() => {
    if (!value) return
    onChange(undefined)
  }, [value, onChange])

  const handleDelete = useCallback(() => {
    if (!value) return
    if (typeof value === 'string') {
      onChange(undefined)
      return
    }
    deleteUploadedFile(value, () => onChange(undefined))
  }, [value, onChange])

  const displayName = value ? getFileName(value) : ''
  const acceptedExtensions = Object.values(accept).flat()

  return (
    <div className={styles.root}>
      {value ? (
        <div className={styles.fileRow}>
          <span className={styles.fileName} title={displayName}>
            {displayName}
          </span>
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            title="Clear field (keeps file in storage)"
          >
            <X size={14} />
            Clear
          </button>
          {typeof value !== 'string' && (
            <button
              type="button"
              onClick={handleDelete}
              className={styles.deleteButton}
              title="Delete file from storage"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`${styles.dropzone} ${
            isDragActive ? styles.dropzoneActive : ''
          } ${isUploading ? styles.dropzoneDisabled : ''}`}
        >
          <input {...getInputProps()} aria-label={ariaLabel} />
          {isUploading ? (
            <>
              <Loader2 size={24} className={styles.spinner} />
              <span>Uploading… Please do not interact</span>
            </>
          ) : (
            <>
              <Upload size={24} className={styles.uploadIcon} />
              <span>
                {isDragActive ? 'Drop the file here' : 'Drop a file or '}
                {!isDragActive && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      open()
                    }}
                    className={styles.browseButton}
                  >
                    click to browse
                  </button>
                )}
              </span>
              {(maxSize > 0 || Object.keys(accept).length) && (
                <span className={styles.hint}>
                  {maxSize > 0 && `Max ${maxSize / 1024 / 1024} MB`}
                  {acceptedExtensions.length
                    ? ` • ${acceptedExtensions.join(', ')}`
                    : ''}
                </span>
              )}
            </>
          )}
        </div>
      )}
      {uploadError && (
        <p className={styles.error} role="alert">
          {uploadError}
        </p>
      )}
    </div>
  )
}

export const FileUploadField = ({
  field,
  value: rawValue,
  onChange,
}: {
  field: FileUploadFieldType
  value: unknown
  onChange: (value: UploadedFile | undefined) => void
}) => {
  const value = rawValue as UploadedFile | undefined
  const accept = field.accept ?? DEFAULT_ACCEPT
  const maxSize = field.maxSize ?? DEFAULT_MAX_SIZE
  const path = field.path ?? DEFAULT_PATH

  return (
    <FieldLabel label={field.label || 'File'} icon={<Image size={16} />}>
      <FileUploadInput
        value={value}
        onChange={onChange}
        accept={accept}
        maxSize={maxSize}
        path={path}
        ariaLabel={field.label || 'Upload file'}
      />
    </FieldLabel>
  )
}
